/**
 * voice-v8 — File-Caption-Modal (Pre.488)
 *
 * Fragt vor dem Hochladen einer Datei (PDF, etc.) eine optionale Caption
 * ab. Parallele Komponente zu image-caption-modal, aber:
 *   - Datei-Icon + Name + Größe statt Bild-Preview
 *   - Quick-Suggestions passend zu Dokumenten ("Fasse zusammen", "Lies vor")
 *
 * Ersatz für das hässliche `window.prompt()` mit langem Datei-Namen.
 */

import { haengeMikrofonAn } from "./feld-mikrofon.js";
import { wachsendesTextfeld } from "./sheet-ui.js";
import { zeichen } from "./zeichen.js";

const SVG_NS = "http://www.w3.org/2000/svg";

interface FileCaptionResult {
  caption: string;
  cancelled: boolean;
  /**
   * Nur im Vorgang: wohin der ganze Schwung soll. null = kein Projekt.
   * (23.09.2026, Ticket 2498)
   */
  projekt?: VorgangProjekt | null;
  /**
   * Nur im Einzel-Dialog: „Weitere hinzufügen" brachte diese Dateien. Der
   * Aufrufer macht aus der einen und ihnen EINEN Vorgang.
   * (23.09.2026, Ticket 2498)
   */
  weitere?: File[];
}

export interface VorgangProjekt {
  id: string;
  name: string;
}

/** Eine Datei, wie der Dialog sie zeigt. */
export interface VorgangDateiAnzeige {
  name: string;
  groesse: number;
}

/**
 * Mehrere Dateien in EINEM Dialog. (23.09.2026, Ticket 2498)
 *
 * Martin: *„eine ganze Serie an Audiospuren auf einmal übergeben"*. Bis
 * dahin kannte dieser Dialog genau einen Dateinamen — wer drei Dateien
 * einfügte, bekam drei Dialoge und drei getrennte Antworten.
 */
interface FileCaptionVorgang {
  dateien: ReadonlyArray<VorgangDateiAnzeige>;
  /** Vorgabe = das gerade aktive Projekt, sonst null. */
  projektVorgabe: VorgangProjekt | null;
  /** Fuer „Anderes Projekt …" — erst beim Tippen geladen. */
  projekteLaden: () => Promise<VorgangProjekt[]>;
  /**
   * „Weitere hinzufügen". Am iPhone kommt EINE Auswahl nur aus EINER
   * Quelle (Fotos ODER Dateien) — PDF, Audio und Bild in einem Zug gehen
   * nur in mehreren Schritten. Liefert die neue, vollstaendige Liste.
   *
   * ⚠️ Muss seinen Dateidialog SOFORT oeffnen, vor dem ersten await — sonst
   *    ist die Geste verbraucht und iOS oeffnet nichts.
   */
  weitere?: () => Promise<ReadonlyArray<VorgangDateiAnzeige>>;
  hoechstens: number;
}

interface FileCaptionOpts {
  filename: string;
  sizeBytes: number;
  mimeType: string;
  defaultCaption?: string;
  /** Gesetzt = mehrere Dateien, EIN Vorgang. Ohne = wie bisher, eine Datei. */
  vorgang?: FileCaptionVorgang;
  /**
   * Nur im Einzel-Dialog: „Weitere hinzufügen". (23.09.2026, Ticket 2498)
   *
   * Am iPhone kommt eine Auswahl nur aus EINER Quelle. Ohne diesen Knopf
   * ging „1 PDF + Fotos" oder „1 Sprachnachricht + Fotos" nie als EIN
   * Vorgang — die eine Datei nahm diesen Dialog und ging allein los.
   * Bringt die Wahl Dateien, endet der Dialog mit `weitere`.
   *
   * ⚠️ Muss seinen Dateidialog SOFORT oeffnen, vor dem ersten await.
   */
  weitereWaehlen?: () => Promise<File[]>;
}

const SUGGESTIONS: ReadonlyArray<string> = [
  "Fasse zusammen",
  "Was steht drin?",
  "Lies das vor",
  "Übersetze auf Englisch",
];

/**
 * Im Vorgang steht das Gemeinsame vorn — darum geht es. (23.09.2026,
 * Ticket 2498) „Lies das vor" faellt weg: zehn Dateien am Stueck vorlesen
 * ist kein Auftrag, den jemand so meint.
 */
const VORGANG_SUGGESTIONS: ReadonlyArray<string> = [
  "Gemeinsam auswerten",
  "Fasse zusammen",
  "Was steht drin?",
  "Übersetze auf Englisch",
];

/** Auch die Karte des Datei-Vorgangs zeigt so — dieselben Zahlen wie hier. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fileIconFor(mime: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  // Datei-Icon mit Ohr-falten oben rechts
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute(
    "d",
    "M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z M14 3v5h5",
  );
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "1.6");
  path.setAttribute("fill", "none");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.appendChild(path);
  // PDF-Label im Icon wenn PDF
  if (mime === "application/pdf") {
    const txt = document.createElementNS(SVG_NS, "text");
    txt.setAttribute("x", "12");
    txt.setAttribute("y", "17");
    txt.setAttribute("text-anchor", "middle");
    txt.setAttribute("font-size", "5");
    txt.setAttribute("font-weight", "700");
    txt.setAttribute("fill", "currentColor");
    txt.textContent = "PDF";
    svg.appendChild(txt);
  }
  return svg;
}

export function askFileCaption(
  opts: FileCaptionOpts,
): Promise<FileCaptionResult> {
  return new Promise<FileCaptionResult>((resolve) => {
    const root = document.createElement("div");
    root.className = "image-caption-modal";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("aria-label", "Datei-Beschreibung");

    const backdrop = document.createElement("div");
    backdrop.className = "image-caption-backdrop";
    root.appendChild(backdrop);

    const card = document.createElement("div");
    card.className = "image-caption-card";
    root.appendChild(card);

    // --- File-Header (Icon + Name + Größe) -----------------------------
    const header = document.createElement("div");
    header.className = "file-caption-header";

    const iconWrap = document.createElement("div");
    iconWrap.className = "file-caption-icon";
    iconWrap.appendChild(fileIconFor(opts.mimeType));
    header.appendChild(iconWrap);

    const meta = document.createElement("div");
    meta.className = "file-caption-meta";
    const name = document.createElement("div");
    name.className = "file-caption-name";
    name.textContent = opts.filename;
    name.title = opts.filename;
    meta.appendChild(name);
    const size = document.createElement("div");
    size.className = "file-caption-size";
    size.textContent = `${formatSize(opts.sizeBytes)} · ${opts.mimeType || "Datei"}`;
    meta.appendChild(size);
    header.appendChild(meta);
    card.appendChild(header);

    // --- Vorgang: Namensliste + „Weitere hinzufügen" (Ticket 2498) -------
    const vorgang = opts.vorgang;
    let dateien: ReadonlyArray<VorgangDateiAnzeige> = vorgang?.dateien ?? [];
    const liste = document.createElement("ol");
    liste.className = "file-caption-liste";
    const weitereKnopf = document.createElement("button");
    weitereKnopf.type = "button";
    weitereKnopf.className = "btn btn--ghost btn--sm file-caption-weitere";
    const beschrifteWeitere = (text: string): void => {
      weitereKnopf.innerHTML = zeichen("plus", 16);
      weitereKnopf.append(text);
    };

    // --- Textarea -----------------------------------------------------
    const label = document.createElement("label");
    label.className = "icm-label";
    // Eigener Knoten fuer den Text: im Vorgang aendert er sich mit der Liste,
    // und textContent am <label> wuerde Feld und Mikro-Knopf mitloeschen.
    const labelText = document.createElement("span");
    labelText.textContent = "Was soll ich mit der Datei tun? (optional)";
    label.appendChild(labelText);

    /** Kopf, Liste und Frage folgen der Liste — sie waechst mit „Weitere". */
    const zeichneVorgang = (): void => {
      if (!vorgang) return;
      const n = dateien.length;
      name.textContent = `${n} Dateien`;
      name.title = name.textContent;
      const summe = dateien.reduce((s, d) => s + d.groesse, 0);
      size.textContent = `${formatSize(summe)} zusammen · eine gemeinsame Antwort`;
      liste.replaceChildren(
        ...dateien.map((d) => {
          const li = document.createElement("li");
          li.className = "file-caption-datei";
          const dn = document.createElement("span");
          dn.className = "file-caption-datei-name";
          dn.textContent = d.name;
          dn.title = d.name;
          const dg = document.createElement("span");
          dg.className = "file-caption-datei-groesse";
          dg.textContent = formatSize(d.groesse);
          li.append(dn, dg);
          return li;
        }),
      );
      labelText.textContent = `Was soll ich mit den ${n} Dateien tun? (optional)`;
      const voll = n >= vorgang.hoechstens;
      weitereKnopf.disabled = voll;
      beschrifteWeitere(
        voll ? `Höchstens ${vorgang.hoechstens} Dateien` : "Weitere hinzufügen",
      );
    };
    if (vorgang) {
      card.appendChild(liste);
      if (vorgang.weitere) {
        const weitere = vorgang.weitere;
        weitereKnopf.addEventListener("click", () => {
          // Ohne await davor: der Dateidialog muss in DIESER Geste aufgehen.
          void weitere().then((neu) => {
            dateien = neu;
            zeichneVorgang();
          });
        });
        card.appendChild(weitereKnopf);
      }
    } else if (opts.weitereWaehlen) {
      // Einzel-Dialog (Ticket 2498): kommen Dateien dazu, wird es ein Vorgang
      // — der Aufrufer oeffnet dessen Dialog, der Auftrag geht mit.
      const waehlen = opts.weitereWaehlen;
      beschrifteWeitere("Weitere hinzufügen");
      weitereKnopf.addEventListener("click", () => {
        // Ohne await davor: der Dateidialog muss in DIESER Geste aufgehen.
        void waehlen().then((neu) => {
          if (neu.length === 0) return;
          finish({ caption: textarea.value.trim(), cancelled: false, weitere: neu });
        });
      });
      card.appendChild(weitereKnopf);
    }
    card.appendChild(label);

    const textarea = document.createElement("textarea");
    textarea.className = "icm-textarea";
    textarea.rows = 2;
    textarea.placeholder = vorgang
      ? "z. B. Werte sie gemeinsam aus …"
      : "z. B. Fasse zusammen …";
    textarea.value = opts.defaultCaption ?? "";
    label.appendChild(textarea);

    // 2026-08-30: gerechnet aus den ECHTEN Werten des Feldes, nicht aus
    // festen Zahlen — die alte Rechnung (22 x 6 + 16 = 148) lag 4,5 Punkte
    // zu tief und schnitt die sechste Zeile an.
    const autoResize = wachsendesTextfeld(textarea, 6);

    // --- Diktier-Knopf -------------------------------------------------
    // 2026-07-29 (Martin: "Fehlt übrigens das Mikrofonzeichen, damit ich es
    // auch transkribieren kann"): Im Hauptfeld kann man diktieren, hier
    // nicht — dabei ist gerade dieser Dialog einer, in dem man unterwegs
    // schnell etwas sagen möchte ("fasse die Seiten 3 bis 5 zusammen").
    // Dieselbe Live-Erkennung wie im Hauptfeld, nur an dieses Feld gebunden.
    const mikroKnopf = haengeMikrofonAn(textarea, {
      klasse: "icm-mic",
      beiText: autoResize,
    }).knopf;
    mikroKnopf.setAttribute("aria-label", "Anweisung diktieren");
    label.appendChild(mikroKnopf);

    // --- Quick-Suggestions -------------------------------------------
    const chips = document.createElement("div");
    chips.className = "icm-chips";
    const chipButtons: HTMLButtonElement[] = [];
    for (const text of vorgang ? VORGANG_SUGGESTIONS : SUGGESTIONS) {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "icm-chip";
      chip.textContent = text;
      chip.addEventListener("click", () => {
        textarea.value = text;
        autoResize();
        textarea.focus();
        const len = textarea.value.length;
        textarea.setSelectionRange(len, len);
      });
      chips.appendChild(chip);
      chipButtons.push(chip);
    }
    card.appendChild(chips);

    // --- Vorgang: wohin der ganze Schwung soll (Ticket 2498) ------------
    // Vorgabe ist das aktive Projekt. Chips statt eines zweiten Blatts:
    // selectSheet laege UNTER diesem Dialog (z-index 950 gegen 980).
    let projekt: VorgangProjekt | null = vorgang?.projektVorgabe ?? null;
    if (vorgang) {
      const zeile = document.createElement("div");
      zeile.className = "file-caption-projekt";
      zeile.setAttribute("role", "group");
      zeile.setAttribute("aria-label", "Ablegen in");
      const titel = document.createElement("span");
      titel.className = "file-caption-projekt-titel";
      titel.textContent = "Ablegen in";
      titel.setAttribute("aria-hidden", "true");
      const wahl = document.createElement("div");
      wahl.className = "icm-chips";
      zeile.append(titel, wahl);

      const markiere = (): void => {
        for (const b of wahl.querySelectorAll<HTMLButtonElement>("[data-projekt]")) {
          const an = b.dataset.projekt === (projekt?.id ?? "");
          b.classList.toggle("icm-chip--active", an);
          b.setAttribute("aria-pressed", an ? "true" : "false");
        }
      };
      const projektChip = (
        text: string,
        wert: VorgangProjekt | null,
      ): HTMLButtonElement => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "icm-chip";
        b.textContent = text;
        b.dataset.projekt = wert?.id ?? "";
        b.addEventListener("click", () => {
          projekt = wert;
          markiere();
        });
        return b;
      };
      const vorgabe = vorgang.projektVorgabe;
      if (vorgabe) wahl.appendChild(projektChip(`„${vorgabe.name}“`, vorgabe));
      wahl.appendChild(projektChip("Kein Projekt", null));
      const anderes = document.createElement("button");
      anderes.type = "button";
      anderes.className = "icm-chip";
      anderes.textContent = "Anderes Projekt …";
      anderes.addEventListener("click", () => {
        anderes.disabled = true;
        void vorgang
          .projekteLaden()
          .catch((): VorgangProjekt[] => [])
          .then((alle) => {
            const rest = alle.filter((p) => p.id !== vorgabe?.id);
            if (rest.length === 0) {
              const leer = document.createElement("span");
              leer.className = "file-caption-projekt-leer";
              leer.textContent = "Keine weiteren Projekte";
              anderes.replaceWith(leer);
              return;
            }
            anderes.replaceWith(
              ...rest.map((p) => projektChip(`„${p.name}“`, p)),
            );
            markiere();
          });
      });
      wahl.appendChild(anderes);
      markiere();
      card.appendChild(zeile);
      zeichneVorgang();
    }

    // --- Buttons ------------------------------------------------------
    const actions = document.createElement("div");
    actions.className = "icm-actions";

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "icm-btn icm-btn--ghost";
    cancelBtn.textContent = "Abbrechen";
    actions.appendChild(cancelBtn);

    const sendBtn = document.createElement("button");
    sendBtn.type = "button";
    sendBtn.className = "icm-btn icm-btn--primary";
    sendBtn.textContent = "Senden";
    actions.appendChild(sendBtn);

    card.appendChild(actions);

    // --- Cleanup ------------------------------------------------------
    let settled = false;
    const finish = (result: FileCaptionResult): void => {
      if (settled) return;
      settled = true;
      document.removeEventListener("keydown", onKeyDown, true);
      backdrop.removeEventListener("click", onCancel);
      cancelBtn.removeEventListener("click", onCancel);
      sendBtn.removeEventListener("click", onSubmit);
      textarea.removeEventListener("keydown", onTextareaKey);
      textarea.removeEventListener("input", autoResize);
      for (const chip of chipButtons) {
        chip.replaceWith(chip.cloneNode(true));
      }
      if (root.parentNode) root.parentNode.removeChild(root);
      resolve(result);
    };

    const onSubmit = (): void => {
      finish({
        caption: textarea.value.trim(),
        cancelled: false,
        ...(vorgang ? { projekt } : {}),
      });
    };
    const onCancel = (): void => {
      finish({ caption: "", cancelled: true });
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    };
    const onTextareaKey = (e: KeyboardEvent): void => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        onSubmit();
      }
    };

    backdrop.addEventListener("click", onCancel);
    cancelBtn.addEventListener("click", onCancel);
    sendBtn.addEventListener("click", onSubmit);
    textarea.addEventListener("keydown", onTextareaKey);
    document.addEventListener("keydown", onKeyDown, true);

    document.body.appendChild(root);
    requestAnimationFrame(() => {
      autoResize();
      textarea.focus();
      const len = textarea.value.length;
      textarea.setSelectionRange(len, len);
    });
  });
}
